/*
 * prntd-quickadd.js
 *
 * One add-to-cart control, attached to every product card on the store.
 *
 * Why a progressive enhancement rather than an edit to each card: this theme
 * renders product cards from one snippet in at least four places (product
 * recommendations, the cart page, collection grids, the bundle drawer) and the
 * search hero renders its own tile markup on top of that. Enhancing the cards
 * in place means the behaviour is identical everywhere and stays identical
 * when a section is added.
 *
 * The control is two buttons, because the size choice is the only decision and
 * most people do not want to make it:
 *   +       adds the default size straight to the cart
 *   pencil  opens the size list on the card
 *
 * The default size is inferred rather than assumed: whatever is selected on
 * the product page you are standing on, else the size you already have most of
 * in the cart, else the cheapest. Getting this right is what makes a one tap
 * add honest instead of a guess the customer has to undo.
 */
(function () {
  'use strict';

  /* Size chips, keyed by the variant title with the spaces taken out. The
     product page carries the same two labels on its own size picker, so a
     shopper who opens the page after using the popup reads the same advice. */
  var SIZE_TAGS = {
    '12x18': 'Most common',
    '16x24': 'Best value',
  };

  /* The theme renders product cards under several modifiers: .card--product
     in grids and recommendations, .card--row in the cart page upsell. Rather
     than chase each one, match .card broadly and let handleFrom() reject
     anything that is not a product: a blog or page card has no /products/
     link, so it is skipped without a special case. */
  var CARD_SELECTORS = ['.card', '.sh__tile', '.slg-card', '[data-qa-card]'];
  var EXCLUDE_HANDLE = /^shipping-protection/;
  var CUSTOM_HANDLE = /(^|-)custom(-|$)/;
  var cache = {};
  var defaultSize = null;

  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function escapeHtml(t) {
    return String(t == null ? '' : t)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* Ask the CDN for the size actually being shown. Shopify accepts a _<w>x
     suffix before the extension, and a query string on the URL survives it. */
  function imgSrc(url, w) {
    if (!url) return '';
    return escapeHtml(String(url).replace(/(\.[a-z]+)(\?|$)/i, '_' + w + 'x$1$2'));
  }

  function handleFrom(el) {
    var a = el.matches('a[href*="/products/"]') ? el : el.querySelector('a[href*="/products/"]');
    if (!a) return null;
    var m = a.getAttribute('href').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }


  /* 6x9 is the club-only size, sold only on /pages/club. Every product JSON
     this file reads is passed through here first, so the size list, the
     default pick and every price ignore it (see snippets/prntd-is-6x9). */
  function isClubSize(v) {
    var t = String((v && (v.title || v.public_title || v.variant_title)) || '').split(' / ')[0]
      .toLowerCase().replace(/inches|inch|in|\s|"|\u201d/g, '');
    return t === '6x9';
  }
  function stripClub(p) {
    if (!p || !p.variants) return p;
    var keep = p.variants.filter(function (v) { return !isClubSize(v); });
    if (!keep.length || keep.length === p.variants.length) return p;
    p.variants = keep;
    var prices = keep.map(function (v) { return v.price; });
    p.price = p.price_min = Math.min.apply(null, prices);
    p.price_max = Math.max.apply(null, prices);
    p.price_varies = p.price_min !== p.price_max;
    if (p.options && p.options[0] && p.options[0].values) {
      p.options[0].values = p.options[0].values.filter(function (val) { return !isClubSize({ title: val }); });
    }
    return p;
  }

  function getProduct(handle) {
    if (cache[handle]) return cache[handle];
    cache[handle] = fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(new Error(r.status)); })
      .then(stripClub);
    return cache[handle];
  }

  /* ---------- working out which size to default to ---------- */

  function sizeOnThisPage() {
    var picked = document.querySelector('.pdp-v2__size[aria-checked="true"], .pdp-v2__size.is-selected');
    if (picked) {
      var t = (picked.getAttribute('data-size') || picked.textContent || '').trim().split('\n')[0].trim();
      if (t) return t;
    }
    var radio = document.querySelector('input[name*="Size"]:checked, input[name*="size"]:checked');
    if (radio && radio.value) return radio.value.trim();
    return null;
  }

  function sizeInCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) {
        if (!c || !c.items || !c.items.length) return null;
        var tally = {};
        c.items.forEach(function (i) {
          var t = (i.variant_title || '').trim();
          if (t) tally[t] = (tally[t] || 0) + i.quantity;
        });
        var best = null;
        Object.keys(tally).forEach(function (k) { if (!best || tally[k] > tally[best]) best = k; });
        return best;
      })
      .catch(function () { return null; });
  }

  function resolveDefaultSize() {
    var here = sizeOnThisPage();
    if (here) { defaultSize = here; return Promise.resolve(here); }
    return sizeInCart().then(function (s) { defaultSize = s; return s; });
  }

  function pickVariant(product) {
    var avail = product.variants.filter(function (v) { return v.available; });
    var list = avail.length ? avail : product.variants;
    if (defaultSize) {
      for (var i = 0; i < list.length; i++) {
        if ((list[i].title || '').trim() === defaultSize) return list[i];
      }
    }
    // Cheapest is the honest fallback: it is the lowest commitment and the
    // size the overwhelming majority of this catalogue sells at.
    return list.slice().sort(function (a, b) { return a.price - b.price; })[0];
  }

  /* ---------- the reassurance ---------- */

  function cartIcon() {
    return document.querySelector('.header__icons a[href*="/cart"], [data-cart-icon], .cart-icon');
  }

  function flyToCart(fromEl) {
    var img = fromEl.closest('*').querySelector('img');
    var target = cartIcon();
    if (!img || !target || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var a = img.getBoundingClientRect();
    var b = target.getBoundingClientRect();
    var ghost = img.cloneNode();
    ghost.className = 'pqa-ghost';
    ghost.style.cssText = 'position:fixed;z-index:9999;pointer-events:none;border-radius:4px;'
      + 'left:' + a.left + 'px;top:' + a.top + 'px;width:' + a.width + 'px;height:' + a.height + 'px;'
      + 'transition:transform .55s cubic-bezier(.5,-0.2,.3,1),opacity .55s ease;';
    document.body.appendChild(ghost);
    var dx = (b.left + b.width / 2) - (a.left + a.width / 2);
    var dy = (b.top + b.height / 2) - (a.top + a.height / 2);
    requestAnimationFrame(function () {
      ghost.style.transform = 'translate(' + dx + 'px,' + dy + 'px) scale(.08) rotate(-8deg)';
      ghost.style.opacity = '0.25';
    });
    setTimeout(function () {
      ghost.remove();
      target.classList.add('pqa-bump');
      setTimeout(function () { target.classList.remove('pqa-bump'); }, 420);
    }, 560);
  }

  function buzz() {
    if (navigator.vibrate) { try { navigator.vibrate(12); } catch (e) { /* not fatal */ } }
  }

  /* ---------- the rating floor ----------
   *
   * A product showing 1.0 from a single review is worse than no rating at
   * all: it is one person's opinion presented with the authority of a score,
   * and on a catalogue of 3,401 posters where only 307 carry any reviews it
   * is mostly noise. Anything under three stars is therefore replaced with
   * the store's own rating, which is measured across every review the shop
   * has and is the honest thing to show in its place.
   *
   * Done in script rather than Liquid because the same stars are rendered by
   * the product card snippet, the cart, the recommendations and two apps, and
   * only some of those files are ours. The star element carries the value in
   * a CSS custom property, which is the one thing all of them share.
   */
  var STORE_RATING = 4.8;
  var MIN_RATING = 3;

  function ratingOf(stars) {
    var v = parseFloat(stars.style.getPropertyValue('--rating'));
    if (!isNaN(v)) return v;
    var host = stars.closest('[data-rating]');
    if (host) {
      var d = parseFloat(host.getAttribute('data-rating'));
      if (!isNaN(d)) return d;
    }
    var label = (stars.closest('.rating') || stars).getAttribute('aria-label') || '';
    var m = label.match(/([0-9]+(?:\.[0-9]+)?)/);
    return m ? parseFloat(m[1]) : NaN;
  }

  function applyRatingFloor(root) {
    var scope = root && root.querySelectorAll ? root : document;

    /* Text ratings, which is what the grids on this store actually render:
       a star glyph then "1.0 (1)". Handled at source in the grid section too,
       so there is no flash of the wrong number; this catches the same markup
       wherever an app or a drawer reproduces it. */
    scope.querySelectorAll('.slg-rating:not([data-prntd-floor])').forEach(function (el) {
      el.setAttribute('data-prntd-floor', '1');
      var m = (el.textContent || '').match(/([0-9]+(?:\.[0-9]+)?)/);
      if (!m) return;
      var v = parseFloat(m[1]);
      if (isNaN(v) || v <= 0 || v >= MIN_RATING) return;
      var star = el.querySelector('i');
      el.textContent = '';
      if (star) el.appendChild(star);
      el.appendChild(document.createTextNode(String(STORE_RATING)));
      el.setAttribute('title', 'Store rating. This poster has too few reviews to score on its own.');
    });

    scope.querySelectorAll('.rating__stars:not([data-prntd-floor])').forEach(function (stars) {
      stars.setAttribute('data-prntd-floor', '1');
      var v = ratingOf(stars);
      if (isNaN(v) || v <= 0 || v >= MIN_RATING) return;

      // Swap the stars to the store's score.
      stars.style.setProperty('--rating', String(STORE_RATING));
      var box = stars.closest('.rating');
      if (box) {
        box.setAttribute('aria-label', STORE_RATING + ' out of 5 stars across the store');
        box.setAttribute('title', 'Store rating. This poster has too few reviews to score on its own.');
      }
      /* The count beside it belongs to the product, so it would now be
         counting the wrong thing. It is removed rather than rewritten: the
         store total is not useful next to a product card. */
      var host = box || stars;
      var sib = host.nextElementSibling;
      if (sib && sib.classList.contains('rating__count')) sib.remove();
    });
  }

  /* Re-run by scan(), which the section's own MutationObserver already calls
     whenever a grid paginates or a drawer renders more cards. A second
     observer here would be doing the same work twice. */

  /* ---------- adding ---------- */

  /* This theme has FOUR separate ways of being told the cart changed, and
     none of them listens to any of the others:

       header count   re-rendered from data.sections['cart-icon-bubble']
       cart drawer    re-renders itself from its own section html
       cart PAGE      listens for 'dispatch:cart-drawer:refresh'
       our own bars   listen for 'cart:refresh'

     Quick add used to fire only the last one. That is why the shipping bar
     moved the instant you tapped while the cart itself sat stale until a
     page load: the bar was the only thing listening. All four are driven
     below. */
  function refreshCartUI(data) {
    /* Same two lines as the theme's ProductForm.updateCartIcon, inlined
       because that class is block scoped and not reachable from here. */
    try {
      var bubble = document.getElementById('cart-icon-bubble');
      if (bubble && data && data.sections && data.sections['cart-icon-bubble']) {
        bubble.innerHTML = data.sections['cart-icon-bubble'];
      }
    } catch (e) { /* a stale count is survivable, a thrown error is not */ }

    try {
      var drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.renderContents === 'function' && data && data.sections) {
        /* false: never yank the drawer open on a quick add. The whole point
           of this control is adding several posters in a row without being
           interrupted, and the tick plus the fly animation already confirm
           it landed. */
        drawer.renderContents(data, false);
      } else if (window.location.pathname === (window.theme && theme.routes && theme.routes.cart)) {
        /* No drawer, so we are on the cart page itself. cart-items listens
           for this one specifically. */
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
      }
    } catch (e) { /* as above */ }

    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    window.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  function addVariant(card, btn, variant) {
    btn.disabled = true;
    /* Green on the tap, not on the response.
       The add takes a few hundred milliseconds on the wire, and spending
       that as a dimmed "busy" button made the best part of the interaction
       feel slow: the tick is the reward and it was arriving last. It is shown
       immediately and taken back only if the request actually fails, which is
       the rare case and already has its own red state. */
    btn.dataset.state = 'done';
    btn.setAttribute('aria-label', 'Added');

    /* The theme rebuilds its cart UI from section HTML returned by the add
       request itself, so the sections have to be ASKED for here. The old
       call sent plain JSON with no `sections` key, so the response carried
       nothing to render and the cart had no way to update short of a reload.
       Requesting them means switching from a JSON body to FormData, which is
       the shape the sections API expects. */
    var sections = ['cart-icon-bubble'];
    var drawerEl = document.querySelector('cart-drawer');
    var drawerSection = drawerEl && drawerEl.closest ? drawerEl.closest('.shopify-section') : null;
    if (drawerSection && drawerSection.id) {
      sections.push(drawerSection.id.replace('shopify-section-', ''));
    }

    var body = new FormData();
    body.append('id', variant.id);
    body.append('quantity', 1);
    body.append('sections', sections.join(','));
    body.append('sections_url', window.location.pathname);

    var addUrl = (window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add.js';

    return fetch(addUrl, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(new Error(r.status)); })
      .then(function (data) {
        buzz();
        flyToCart(btn);
        // Already green from the tap; this only re-asserts it in case the
        // reset below fired first on a very slow response.
        btn.dataset.state = 'done';
        refreshCartUI(data);
        /* 1600, not 1200. The running bundle bar reveals itself only once its
           own cart fetch returns, which on a desktop connection lands around
           1.2 seconds, so the tick was fading at the exact moment the bar
           appeared and the two halves of the confirmation never overlapped.
           The button is re-enabled on the same timer; 1.6s is still short
           enough to add several posters in a row without waiting on it. */
        setTimeout(function () { btn.dataset.state = ''; btn.disabled = false; }, 1600);
      })
      .catch(function () {
        btn.dataset.state = 'error';
        btn.disabled = false;
        setTimeout(function () { btn.dataset.state = ''; }, 2000);
      });
  }

  /* Anchor to the IMAGE, never to the card. Each surface wraps its image in a
     different element (.card__media in theme grids, .sh__frame in the search
     hero, .slg-shot on the collection landing), and attaching to the card
     drops the controls on the price instead. The size popup uses the same
     anchor as the buttons, or it escapes the card entirely and floats off to
     one side, which is exactly what it did until this was shared. */
  function mediaOf(card) {
    var media = card.querySelector('.card__media, .sh__frame, .slg-shot');
    if (!media) {
      var img = card.querySelector('img');
      media = img && img.parentElement && card.contains(img.parentElement) ? img.parentElement : card;
    }
    return media;
  }

  /* On a phone the grid is three posters to a row, about 117px each, and the
     size list is wider and taller than a whole tile. Anchored to the card it
     covered two neighbours and the filter pills above, so nothing on screen
     said which poster the prices belonged to.

     So phones get a sheet from the bottom of the screen instead: one panel,
     opaque, with the poster's thumbnail and name at the top of it, over a
     dimmed page. Desktop keeps the anchored popup, where there is room for it
     to sit beside its own card and point at it. */
  function isSheet() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  function openSizes(card, handle, btn, opener) {
    function closeBox(el) {
      var host = el.parentElement;
      var veil = document.querySelector('.pqa__veil');
      if (veil) veil.remove();
      document.documentElement.classList.remove('pqa-locked');
      el.remove();
      if (host && host.dataset.pqaOverflow) {
        host.style.overflow = host.dataset.pqaOverflow === 'visible' ? '' : host.dataset.pqaOverflow;
      }
    }
    var existing = card.querySelector('.pqa__sizes') || document.querySelector('.pqa__sizes[data-card="' + handle + '"]');
    if (existing) { closeBox(existing); return; }
    // Only one at a time, whichever card it was opened from.
    var stray = document.querySelector('.pqa__sizes');
    if (stray) closeBox(stray);
    getProduct(handle).then(function (p) {
      var box = document.createElement('div');
      box.className = 'pqa__sizes';
      /* Name the product in the popup. It overhangs the tile by design, so on
         a two column phone grid it sits over the poster next to it, and
         without a heading there was nothing on screen saying which of the two
         you were about to buy. */
      var sheet = isSheet();
      box.dataset.card = handle;
      if (sheet) box.classList.add('pqa__sizes--sheet');

      var head = document.createElement('p');
      head.className = 'pqa__sizesHead';
      head.textContent = p.title || 'Choose a size';
      if (sheet) {
        /* The thumbnail is the whole point of the sheet: it is the only thing
           that proves which of the three posters in the row you tapped. */
        head.classList.add('pqa__sizesHead--sheet');
        var shot = p.featured_image || (p.images && p.images[0]);
        head.innerHTML = (shot ? '<img class="pqa__sizesShot" src="' + imgSrc(shot, 120) + '" alt="">' : '')
          + '<span class="pqa__sizesName">' + escapeHtml(p.title || 'Choose a size') + '</span>'
          + '<span class="pqa__sizesHint">Pick a size</span>';
      }
      box.appendChild(head);

      var list = document.createElement('div');
      list.className = 'pqa__sizesList';
      p.variants.forEach(function (v) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'pqa__size';
        /* Two sizes are named, and only two: 12x18 is in more orders than any
           other size (796 orders from June to August against 725 for 11x16.5),
           and 16x24 is the one the product pages already call the best value,
           so the popup and the page say the same thing. Any more chips than
           this and none of them mean anything. */
        var key = String(v.title).replace(/\s/g, '').toLowerCase();
        var tag = SIZE_TAGS[key];
        b.innerHTML = '<span>' + v.title
          + (tag ? ' <em class="pqa__common">' + tag + '</em>' : '')
          + '</span><span>' + money(v.price) + '</span>';
        if (tag) b.classList.add('is-common');
        if (!v.available) { b.disabled = true; b.classList.add('is-out'); }
        b.addEventListener('click', function (e) {
          e.preventDefault(); e.stopPropagation();
          defaultSize = v.title;
          closeBox(box);
          addVariant(card, btn, v);
        });
        list.appendChild(b);
      });
      box.appendChild(list);
      if (sheet) {
        /* Appended to the body, not the card. A fixed element inside a
           transformed or clipping ancestor is positioned against that
           ancestor instead of the viewport, and the card grids here do both. */
        var veil = document.createElement('div');
        veil.className = 'pqa__veil';
        veil.addEventListener('click', function () { closeBox(box); });
        document.body.appendChild(veil);
        document.body.appendChild(box);
        document.documentElement.classList.add('pqa-locked');
        return;
      }

      var anchor = mediaOf(card);
      if (getComputedStyle(anchor).position === 'static') anchor.style.position = 'relative';
      /* The media box clips to rounded corners, which would cut the popup off
         at the edge of the artwork. Let it overhang while open, and put the
         clipping back when it closes. */
      if (!anchor.dataset.pqaOverflow) {
        anchor.dataset.pqaOverflow = getComputedStyle(anchor).overflow;
      }
      anchor.style.overflow = 'visible';
      box.dataset.anchored = '1';
      anchor.appendChild(box);

      /* Keep it on screen. The popup is wider than a tile by design, so on a
         tile in the last column it hung off the right edge of the phone. It is
         pinned to the anchor's right edge, and increasing that offset pulls it
         back inside. Measured after insertion rather than guessed, because the
         tile position depends on the grid and the breakpoint. */
      var pad = 8;
      var r = box.getBoundingClientRect();
      var vw = document.documentElement.clientWidth;
      if (r.right > vw - pad) {
        // Hanging off the right edge: increase the offset to pull it left.
        box.style.right = (6 + (r.right - (vw - pad))) + 'px';
      } else if (r.left < pad) {  // eslint-disable-line no-empty
        /* Hanging off the LEFT edge, which is what happens on a first column
           tile, since the popup is wider than the tile and grows leftward from
           its right edge. The offset has to go NEGATIVE to push it back to the
           right. Clamping this at zero was the bug: it moved the box six
           pixels and left the rest of it off screen. */
        box.style.right = (6 - (pad - r.left)) + 'px';
      }

      /* Point the tail at the button that opened it. Measured after the nudge
         above, not before, so the popup can slide sideways to stay on screen
         and still be visibly attached to its own card. Kept a tail's width in
         from either corner so it never hangs off the rounded edge. */
      if (opener) {
        var ob = opener.getBoundingClientRect();
        var nb = box.getBoundingClientRect();
        var fromRight = nb.right - (ob.left + ob.width / 2);
        box.style.setProperty('--pqa-arrow',
          Math.min(Math.max(fromRight, 14), Math.max(nb.width - 14, 14)) + 'px');
      }
      // Any tap outside closes it, so it never becomes a trap on a phone.
      setTimeout(function () {
        document.addEventListener('click', function close(ev) {
          if (!box.contains(ev.target)) { closeBox(box); document.removeEventListener('click', close); }
        });
      }, 0);
    });
  }

  /* ---------- attaching ---------- */

  function enhance(card) {
    if (card.dataset.pqa) return;
    var handle = handleFrom(card);
    if (!handle) return;
    card.dataset.pqa = '1';

    // The shipping protection add-on is not a poster and should never appear
    // in a poster recommendation. It is sold by the app at checkout.
    if (EXCLUDE_HANDLE.test(handle)) {
      var host = card.closest('li') || card;
      host.setAttribute('hidden', '');
      return;
    }

    var wrap = document.createElement('div');
    wrap.className = 'pqa';

    /* A custom poster is a photo first. Test 2 audit, 2026-10-13: the one
       tap add on its card put it in the cart with no photo, a paid order with
       nothing to print, and the size popup did the same. Its card gets one
       button instead, which goes to the product page where the photo is
       uploaded. */
    if (CUSTOM_HANDLE.test(handle)) {
      var go = document.createElement('a');
      go.className = 'pqa__btn pqa__btn--add';
      go.href = '/products/' + handle;
      go.setAttribute('aria-label', 'Upload your photo');
      go.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" class="pqa__i pqa__i--plus">'
        + '<path d="M12 16V5M7 10l5-5 5 5M5 19h14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      wrap.appendChild(go);
      var cmedia = mediaOf(card);
      if (getComputedStyle(cmedia).position === 'static') cmedia.style.position = 'relative';
      cmedia.appendChild(wrap);
      return;
    }

    var add = document.createElement('button');
    add.type = 'button';
    add.className = 'pqa__btn pqa__btn--add';
    add.setAttribute('aria-label', 'Add to cart');
    add.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" class="pqa__i pqa__i--plus">'
      + '<path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/></svg>'
      + '<svg viewBox="0 0 24 24" aria-hidden="true" class="pqa__i pqa__i--tick">'
      + '<path d="M4 12.5l5 5L20 6.5" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    var edit = document.createElement('button');
    edit.type = 'button';
    edit.className = 'pqa__btn pqa__btn--edit';
    edit.setAttribute('aria-label', 'Choose a size');
    // A ruler, not a pencil. The button changes the SIZE of the print, and a
    // pencil reads as "edit the artwork", which is the one thing it does not do.
    edit.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" class="pqa__i">'
      + '<rect x="2.2" y="7.6" width="19.6" height="8.8" rx="1.4" fill="none" stroke="currentColor" stroke-width="1.8"/>'
      + '<path d="M6.4 7.6v3.4M10 7.6v2.2M13.6 7.6v3.4M17.2 7.6v2.2"'
      + ' stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';

    add.addEventListener('click', function (e) {
      e.preventDefault(); e.stopPropagation();
      /* Green on the tap itself. addVariant sets it too, but it runs behind a
         fetch for the product's variants, so the tick was landing about 200ms
         after the finger. The reward should not queue behind a lookup. */
      add.disabled = true;
      add.dataset.state = 'done';
      add.setAttribute('aria-label', 'Added');
      getProduct(handle).then(function (p) { addVariant(card, add, pickVariant(p)); });
    });
    edit.addEventListener('click', function (e) {
      e.preventDefault(); e.stopPropagation();
      openSizes(card, handle, add, edit);
    });

    wrap.appendChild(edit);
    wrap.appendChild(add);
    /* Anchor to the IMAGE, never to the card. Each surface wraps its image in
       a different element (.card__media in theme grids, .sh__frame in the
       search hero, .slg-shot on the collection landing), and attaching to the
       card instead drops the buttons on top of the price, which is what they
       did on the collection page until this was fixed. Counting the buttons
       said everything was fine; looking at the page said otherwise. */
    var media = mediaOf(card);
    if (getComputedStyle(media).position === 'static') media.style.position = 'relative';
    media.appendChild(wrap);
  }

  /* Shipping Protection is a checkout add-on, not a poster, and it turns up in
     recommendation rows rendered by apps whose markup we do not control and
     cannot style by class. Find it by its product link instead, walk up a few
     levels to whatever wrapper is acting as the card, and hide that. The walk
     is bounded and refuses anything taller than half the viewport, so a bad
     match can never blank out a section of the page. */
  function hideProtection() {
    document.querySelectorAll('a[href*="/products/shipping-protection"]').forEach(function (a) {
      if (a.closest('[data-pqa]')) return;
      var el = a, depth = 0;
      while (el && depth < 4) {
        var parent = el.parentElement;
        if (!parent || parent === document.body) break;
        var r = parent.getBoundingClientRect();
        if (r.height > window.innerHeight * 0.5) break;
        // A card is the wrapper that holds the image and the price together.
        if (parent.querySelector('img') && parent.children.length <= 6) el = parent;
        depth++;
        if (parent === el) continue;
        el = parent;
      }
      if (el && el !== document.body && el.getBoundingClientRect().height <= window.innerHeight * 0.5) {
        el.setAttribute('hidden', '');
        el.style.display = 'none';
      }
    });
  }

  function scan() {
    CARD_SELECTORS.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(enhance);
    });
    hideProtection();
    applyRatingFloor(document);
  }

  function start() {
    resolveDefaultSize().then(scan);
    // Grids on this store paginate and swap in place, so watch for new cards.
    if ('MutationObserver' in window) {
      var pending = null;
      new MutationObserver(function () {
        clearTimeout(pending);
        pending = setTimeout(scan, 120);
      }).observe(document.body, { childList: true, subtree: true });
    }
    ['cart:refresh', 'cart:build'].forEach(function (e) {
      document.addEventListener(e, function () { resolveDefaultSize(); });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
